import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import useAuth from "../../hooks/useAuth";
import {
  DispatcherAccount,
  DispatcherAccountType,
  DispatcherAccountWrite,
  dispatcherPasswordsError,
  dispatcherPasswordsService,
} from "../../api/dispatcherPasswordsService";

function AccountPassword({
  account,
  token,
}: {
  account: DispatcherAccount;
  token: string;
}) {
  const [password, setPassword] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const request = useRef<AbortController>();

  useEffect(() => {
    const hide = () => {
      request.current?.abort();
      setPassword(undefined);
      setLoading(false);
    };
    const timeout = window.setTimeout(hide, 60_000);
    window.addEventListener("blur", hide);
    document.addEventListener("visibilitychange", hide);
    return () => {
      request.current?.abort();
      window.clearTimeout(timeout);
      window.removeEventListener("blur", hide);
      document.removeEventListener("visibilitychange", hide);
    };
  }, [password]);

  const toggle = async () => {
    if (password !== undefined) {
      setPassword(undefined);
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError("");
    try {
      const value = await dispatcherPasswordsService.reveal(
        token,
        account.id,
        controller.signal,
      );
      if (!controller.signal.aborted) setPassword(value);
    } catch (e) {
      if (!controller.signal.aborted) setError(dispatcherPasswordsError(e));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  if (!account.hasPassword) return null;
  return (
    <Stack alignItems="flex-start">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          component="span"
          sx={{
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {password === undefined ? "••••••••" : password}
        </Box>
        <Tooltip
          title={password === undefined ? "Reveal password" : "Hide password"}
        >
          <span>
            <IconButton
              size="small"
              disabled={loading}
              onClick={toggle}
              aria-label={
                password === undefined ? "Reveal password" : "Hide password"
              }
            >
              {loading ? (
                <CircularProgress size={18} />
              ) : password === undefined ? (
                <Visibility />
              ) : (
                <VisibilityOff />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

export default function DispatchersPasswordsPage() {
  const { token, role } = useAuth();
  const canRead = role?.some((r) =>
    ["Operations", "OperationsManager", "OperationsAdmin"].includes(r),
  );
  const canManage = role?.some((r) =>
    ["OperationsManager", "OperationsAdmin"].includes(r),
  );
  const [accounts, setAccounts] = useState<DispatcherAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [accountType, setAccountType] = useState("");
  const [revision, setRevision] = useState(0);
  const [form, setForm] = useState<DispatcherAccountWrite>();
  const [editId, setEditId] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editLoaded, setEditLoaded] = useState(true);
  const [formError, setFormError] = useState("");
  const editRequest = useRef<AbortController>();
  const [deleteAccount, setDeleteAccount] = useState<DispatcherAccount>();
  const [deleting, setDeleting] = useState(false);
  const deletePending = useRef(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setAccounts([]);
    setForm(undefined);
    setDeleteAccount(undefined);
    setDeleteSuccess(false);
    setLoading(true);
    setError("");
    if (token && canRead) {
      dispatcherPasswordsService
        .list(token, controller.signal)
        .then((data) => {
          if (!controller.signal.aborted) setAccounts(data);
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(dispatcherPasswordsError(e));
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    } else setLoading(false);
    return () => {
      controller.abort();
      editRequest.current?.abort();
    };
  }, [token, canRead, revision]);

  const close = () => {
    if (saving) return;
    editRequest.current?.abort();
    setForm(undefined);
    setEditId(undefined);
    setShowPassword(false);
    setFormError("");
    setEditing(false);
  };

  useEffect(() => {
    const hide = () => setShowPassword(false);
    const timeout = window.setTimeout(hide, 60_000);
    window.addEventListener("blur", hide);
    document.addEventListener("visibilitychange", hide);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("blur", hide);
      document.removeEventListener("visibilitychange", hide);
    };
  }, [showPassword]);

  const openEdit = async (account: DispatcherAccount) => {
    if (!token || !canManage) return;
    const controller = new AbortController();
    editRequest.current?.abort();
    editRequest.current = controller;
    setEditId(account.id);
    setForm({
      website: account.website,
      login: account.login,
      password: "",
      accountType: account.accountType ?? null,
    });
    setFormError("");
    setShowPassword(false);
    setEditing(true);
    setEditLoaded(false);
    try {
      const password = await dispatcherPasswordsService.reveal(
        token,
        account.id,
        controller.signal,
      );
      if (!controller.signal.aborted) {
        setForm({
          website: account.website,
          login: account.login,
          password,
          accountType: account.accountType ?? null,
        });
        setEditLoaded(true);
      }
    } catch (e) {
      if (!controller.signal.aborted) setFormError(dispatcherPasswordsError(e));
    } finally {
      if (!controller.signal.aborted) setEditing(false);
    }
  };

  const save = async () => {
    if (!token || !canManage || !form || editing || !editLoaded) return;
    setSaving(true);
    setFormError("");
    try {
      await dispatcherPasswordsService.save(token, form, editId);
      setForm(undefined);
      setShowPassword(false);
      setRevision((value) => value + 1);
    } catch (e) {
      setFormError(dispatcherPasswordsError(e));
    } finally {
      setSaving(false);
    }
  };

  const closeDelete = () => {
    if (deletePending.current) return;
    setDeleteAccount(undefined);
    setDeleteError("");
  };

  const remove = async () => {
    if (!token || !canManage || !deleteAccount || deletePending.current) return;
    const id = deleteAccount.id;
    deletePending.current = true;
    setDeleting(true);
    setDeleteError("");
    setDeleteSuccess(false);
    try {
      await dispatcherPasswordsService.delete(token, id);
      setAccounts((current) => current.filter((account) => account.id !== id));
      setDeleteAccount(undefined);
      setDeleteSuccess(true);
    } catch (e) {
      setDeleteError(dispatcherPasswordsError(e));
    } finally {
      deletePending.current = false;
      setDeleting(false);
    }
  };

  const filtered = accounts.filter(
    (account) =>
      (!accountType ||
        (accountType === "unassigned"
          ? !account.accountType
          : account.accountType === accountType)) &&
      [account.website, account.login].some((value) =>
        value.toLowerCase().includes(search.toLowerCase()),
      ),
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Dispatchers Passwords</Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditId(undefined);
              setFormError("");
              setShowPassword(false);
              setEditLoaded(true);
              setForm({
                website: "",
                login: "",
                password: "",
                accountType: null,
              });
            }}
          >
            Add account
          </Button>
        )}
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search website or email/login"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          select
          label="Filter by account type"
          value={accountType}
          onChange={(e) => setAccountType(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All account types</MenuItem>
          <MenuItem value="C1">C1</MenuItem>
          <MenuItem value="Fox">Fox</MenuItem>
          <MenuItem value="unassigned">Unassigned</MenuItem>
        </TextField>
      </Stack>
      {deleteSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setDeleteSuccess(false)}
        >
          Passwords deleted successfully.
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" onClick={() => setRevision((v) => v + 1)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress aria-label="Loading accounts" />
      ) : (
        !error && (
          <TableContainer component={Paper}>
            <Table aria-label="Dispatcher accounts">
              <TableHead>
                <TableRow>
                  <TableCell>Website (optional)</TableCell>
                  <TableCell>Email / Login</TableCell>
                  <TableCell>Account type</TableCell>
                  <TableCell>Password</TableCell>
                  {canManage && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((account) => (
                  <TableRow key={`${revision}-${account.id}`}>
                    <TableCell
                      sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                    >
                      {account.website}
                    </TableCell>
                    <TableCell
                      sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                    >
                      {account.login}
                    </TableCell>
                    <TableCell>{account.accountType || "Unassigned"}</TableCell>
                    <TableCell>
                      {token && (
                        <AccountPassword account={account} token={token} />
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title="Edit account">
                          <IconButton
                            aria-label="Edit account"
                            onClick={() => openEdit(account)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete passwords">
                          <IconButton
                            aria-label="Delete passwords"
                            color="error"
                            disabled={deleting}
                            onClick={() => {
                              if (!canManage) return;
                              setDeleteAccount(account);
                              setDeleteError("");
                              setDeleteSuccess(false);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!filtered.length && (
                  <TableRow>
                    <TableCell colSpan={canManage ? 5 : 4}>
                      {search || accountType
                        ? "No matching accounts."
                        : "No accounts yet."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
      <Dialog
        open={!!form && !!canManage}
        onClose={close}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editId ? "Edit account" : "Add account"}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          {editing && <CircularProgress aria-label="Loading account" />}
          {form && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                select
                label="Account type"
                value={form.accountType ?? ""}
                disabled={saving || editing}
                onChange={(e) => {
                  setFormError("");
                  setForm({
                    ...form,
                    accountType: (e.target.value ||
                      null) as DispatcherAccountType | null,
                  });
                }}
              >
                <MenuItem value="">Unassigned</MenuItem>
                <MenuItem value="C1">C1</MenuItem>
                <MenuItem value="Fox">Fox</MenuItem>
              </TextField>
              <TextField
                label="Website (optional)"
                value={form.website}
                disabled={saving || editing}
                inputProps={{ maxLength: 2048 }}
                onChange={(e) => {
                  setFormError("");
                  setForm({ ...form, website: e.target.value });
                }}
              />
              <TextField
                label="Email / Login"
                value={form.login}
                disabled={saving || editing}
                autoComplete="off"
                inputProps={{ maxLength: 1024, spellCheck: false }}
                onChange={(e) => {
                  setFormError("");
                  setForm({ ...form, login: e.target.value });
                }}
              />
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                disabled={saving || editing}
                autoComplete="new-password"
                inputProps={{ maxLength: 4096, spellCheck: false }}
                onChange={(e) => {
                  setFormError("");
                  setForm({ ...form, password: e.target.value });
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Reveal password"
                        }
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || editing || !editLoaded}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!deleteAccount && !!canManage}
        onClose={closeDelete}
        aria-labelledby="delete-passwords-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="delete-passwords-title">Delete passwords</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography>
            Delete the saved account and credentials for{" "}
            {deleteAccount?.login || "this account"}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={remove}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete passwords"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
